package ds.pms.tokafull.adminweb

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.decodeFromJsonElement
import kotlin.js.Promise
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.files.File

private val tolerantJson = Json { ignoreUnknownKeys = true }

private fun requireOkOrExplain(path: String, status: Int, rawBody: String) {
    if (status !in 200..299) {
        if (status == 401) {
            error("$path → Session expired or invalid. Please sign in again.")
        }
        val hint = rawBody.take(280).trim().ifEmpty { "(empty body)" }
        error("$path → HTTP $status $hint")
    }
}

private fun encQuery(value: String): String = js("encodeURIComponent")(value).unsafeCast<String>()

private fun buildUrl(baseUrl: String, path: String, query: List<Pair<String, String?>> = emptyList()): String {
    val root = baseUrl.trimEnd('/')
    val p = path.ifBlank { "" }.let { if (it.startsWith("/")) it else "/$it" }
    val parts = query.filter { (_, v) -> v != null && v.isNotBlank() }
        .map { (k, v) -> "$k=${encQuery(v!!)}" }
    return if (parts.isEmpty()) "$root$p" else "$root$p?${parts.joinToString("&")}"
}

private suspend fun readResponseText(response: Response): String {
    val promise = response.asDynamic().text() as Promise<String>
    return promise.await()
}

private fun throwIfHtmlInsteadOfJson(raw: String, where: String) {
    val t = raw.removePrefix("\uFEFF").trimStart()
    if (t.startsWith("<!DOCTYPE", ignoreCase = true) ||
        t.startsWith("<!doctype", ignoreCase = true) ||
        t.startsWith("<html", ignoreCase = true)
    ) {
        error(
            "$where: server returned HTML, not JSON. The dashboard is calling the wrong host/port " +
                "(often the webpack/static port instead of Ktor). " +
                "Set globalThis.ADMIN_API_BASE_URL to your API origin, e.g. http://127.0.0.1:8080 " +
                "or ensure the Kotlin API is running on :8080. Snippet: ${t.take(100).replace('\n', ' ')}"
        )
    }
}

private inline fun <reified T> decodeApiJson(where: String, raw: String): T {
    val t = raw.removePrefix("\uFEFF").trim()
    throwIfHtmlInsteadOfJson(t, where)
    return tolerantJson.decodeFromString(t)
}

/**
 * Uses browser [fetch] + Response.text() so gzip/brotli and Content-Length mismatches from CDNs
 * do not break parsing (Ktor JS HttpClient can still fail here; Accept-Encoding is not fully controllable in browsers).
 */
private suspend fun execFetch(
    url: String,
    method: String,
    body: String? = null,
): Pair<Int, String> {
    val headers = js("{}")
    headers["Accept"] = "application/json"
    if (body != null) {
        headers["Content-Type"] = "application/json"
    }
    val t = AdminSession.getToken()
    if (!t.isNullOrBlank()) {
        headers["Authorization"] = "Bearer $t"
    }
    val init = js("{}")
    init["method"] = method
    init["headers"] = headers
    if (body != null) {
        init["body"] = body
    }
    val response = window.fetch(url, init.unsafeCast<RequestInit>()).unsafeCast<Promise<Response>>().await()
    val status = response.status.toInt()
    val text = readResponseText(response)
    if (status == 401) {
        AdminSession.clear()
    }
    return status to text
}

class AdminApiClient(
    private val baseUrl: String = detectBaseUrl()
) {
    suspend fun login(username: String, password: String): LoginResponseDto {
        val body = tolerantJson.encodeToString(LoginRequestDto(username.trim(), password.trim()))
        val (status, text) = execFetch(buildUrl(baseUrl, "/auth/login"), "POST", body)
        if (status != 200) {
            val hint = text.take(160).trim().ifEmpty { "(empty body)" }
            error(
                when (status) {
                    401, 403 -> "Invalid username or password"
                    502, 503, 504 ->
                        "API is not running (HTTP $status). Redeploy or start the Toka API app in Coolify. $hint"
                    else -> "Sign-in failed (HTTP $status). $hint"
                }
            )
        }
        return decodeApiJson("POST /auth/login", text)
    }

    suspend fun logout() {
        runCatching {
            execFetch(buildUrl(baseUrl, "/auth/logout"), "POST", body = null)
        }
        AdminSession.clear()
    }

    suspend fun getProductsPage(
        page: Int,
        pageSize: Int,
        q: String?,
        size: String? = null,
        mechanism: String? = null,
        grade: String? = null,
        brand: String? = null,
        cast: String? = null,
    ): PagedProductsDto {
        val query = buildList {
            add("page" to page.toString())
            add("page_size" to pageSize.toString())
            if (!q.isNullOrBlank()) add("q" to q.trim())
            if (!size.isNullOrBlank()) add("size" to size.trim())
            if (!mechanism.isNullOrBlank()) add("mechanism" to mechanism.trim())
            if (!grade.isNullOrBlank()) add("grade" to grade.trim())
            if (!brand.isNullOrBlank()) add("brand" to brand.trim())
            if (!cast.isNullOrBlank()) add("cast" to cast.trim())
        }
        val url = buildUrl(baseUrl, "/admin/products", query)
        val (status, text) = execFetch(url, "GET")
        val trimmed = text.trim()
        requireOkOrExplain("GET /admin/products", status, trimmed)
        return when {
            trimmed.startsWith("[") -> {
                val items = decodeApiJson<List<AdminProductSummaryDto>>("GET /admin/products", trimmed)
                PagedProductsDto(
                    items = items,
                    total = items.size.toLong(),
                    page = page.coerceAtLeast(1),
                    pageSize = pageSize
                )
            }
            trimmed.startsWith("{") -> decodeApiJson("GET /admin/products", trimmed)
            else -> error("Unexpected /admin/products response: ${trimmed.take(120)}")
        }
    }

    suspend fun getMatrixPage(page: Int, pageSize: Int): PagedMatrixDto {
        val url = buildUrl(
            baseUrl,
            "/admin/matrix",
            listOf("page" to page.toString(), "page_size" to pageSize.toString())
        )
        val (status, text) = execFetch(url, "GET")
        val trimmed = text.trim()
        requireOkOrExplain("GET /admin/matrix", status, trimmed)
        return when {
            trimmed.startsWith("[") -> {
                val items = decodeApiJson<List<PricingMatrixRowDto>>("GET /admin/matrix", trimmed)
                PagedMatrixDto(
                    items = items,
                    total = items.size.toLong(),
                    page = page.coerceAtLeast(1),
                    pageSize = pageSize
                )
            }
            trimmed.startsWith("{") -> decodeApiJson("GET /admin/matrix", trimmed)
            else -> error("Unexpected /admin/matrix response: ${trimmed.take(120)}")
        }
    }

    suspend fun getModifiersPage(page: Int, pageSize: Int, q: String? = null): ModifiersPageLoad {
        val paths = listOf("/admin/modifiers", "/admin/modifier-list")
        val p = page.coerceAtLeast(1)
        val qParams = mutableListOf(
            "page" to p.toString(),
            "page_size" to pageSize.toString(),
        )
        q?.trim()?.takeIf { it.isNotEmpty() }?.let { qParams += "q" to it }
        for (path in paths) {
            val url = buildUrl(baseUrl, path, qParams)
            val (status, raw) = execFetch(url, "GET")
            val text = raw.removePrefix("\uFEFF").trim()
            if (status == 404) continue
            requireOkOrExplain("GET $path", status, text)
            if (text.isEmpty()) {
                return ModifiersPageLoad(PagedModifiersDto(emptyList(), 0L, p, pageSize), endpointMissing = false)
            }
            throwIfHtmlInsteadOfJson(text, "GET $path")
            val element = runCatching { tolerantJson.parseToJsonElement(text) }.getOrElse { e ->
                error("GET $path invalid JSON (${e.message?.take(120)}): ${text.take(200)}")
            }
            val dto = when (element) {
                is JsonArray -> {
                    val items = tolerantJson.decodeFromJsonElement<List<ModifierRowDto>>(element)
                    PagedModifiersDto(items, items.size.toLong(), p, pageSize)
                }
                is JsonObject -> tolerantJson.decodeFromJsonElement<PagedModifiersDto>(element)
                else -> error("GET $path: expected JSON array or object, got ${element::class.simpleName}")
            }
            return ModifiersPageLoad(dto, endpointMissing = false)
        }
        return ModifiersPageLoad(
            PagedModifiersDto(emptyList(), 0L, p, pageSize),
            endpointMissing = true
        )
    }

    suspend fun getUsersPage(page: Int, pageSize: Int): UsersPageLoad {
        val paths = listOf("/admin/users", "/admin/user-list")
        val p = page.coerceAtLeast(1)
        val q = listOf("page" to p.toString(), "page_size" to pageSize.toString())
        for (path in paths) {
            val url = buildUrl(baseUrl, path, q)
            val (status, raw) = execFetch(url, "GET")
            val text = raw.removePrefix("\uFEFF").trim()
            if (status == 404) continue
            requireOkOrExplain("GET $path", status, text)
            if (text.isEmpty()) {
                return UsersPageLoad(PagedUsersDto(emptyList(), 0L, p, pageSize), endpointMissing = false)
            }
            throwIfHtmlInsteadOfJson(text, "GET $path")
            val element = runCatching { tolerantJson.parseToJsonElement(text) }.getOrElse { e ->
                error("GET $path invalid JSON (${e.message?.take(120)}): ${text.take(200)}")
            }
            val dto = when (element) {
                is JsonArray -> {
                    val items = tolerantJson.decodeFromJsonElement<List<AdminUserRowDto>>(element)
                    PagedUsersDto(items, items.size.toLong(), p, pageSize)
                }
                is JsonObject -> tolerantJson.decodeFromJsonElement<PagedUsersDto>(element)
                else -> error("GET $path: expected JSON array or object, got ${element::class.simpleName}")
            }
            return UsersPageLoad(dto, endpointMissing = false)
        }
        return UsersPageLoad(
            PagedUsersDto(emptyList(), 0L, p, pageSize),
            endpointMissing = true
        )
    }

    suspend fun createUser(req: CreateUserRequestDto) {
        val body = tolerantJson.encodeToString(req.copy(username = req.username.trim()))
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/users"), "POST", body)
        if (status in 200..299) return
        if (status == 404 || status == 405) {
            val (s2, t2) = execFetch(buildUrl(baseUrl, "/admin/user"), "POST", body)
            if (s2 in 200..299) return
            requireOk("POST /admin/user", s2, t2)
            return
        }
        requireOk("POST /admin/users", status, text)
    }

    suspend fun patchUser(userId: String, req: PatchUserRequestDto) {
        val id = userId.trim()
        val enc = id.replace(" ", "%20")
        val patchBody = tolerantJson.encodeToString(req)

        // 1) Standard REST endpoint — works on deployed server and locally.
        val (s1, t1) = execFetch(buildUrl(baseUrl, "/admin/users/$enc"), "PATCH", patchBody)
        if (s1 in 200..299) return

        // 2) Body-based POST endpoints for newer server builds (userId in JSON, no UUID in URL).
        val saveBody = UserPatchSaveRequestDto(
            userId = id,
            nameEn = req.nameEn,
            nameUr = req.nameUr,
            addressEn = req.addressEn,
            mobileEn = req.mobileEn,
            userType = req.userType,
            status = req.status,
            password = req.password,
            brandCodes = req.brandCodes,
        )
        val bodyWithId = tolerantJson.encodeToString(saveBody)
        for (path in listOf("/admin/user/save", "/admin/users/save", "/admin/user", "/admin/users")) {
            val (status, text) = execFetch(buildUrl(baseUrl, path), "POST", bodyWithId)
            if (status in 200..299) return
            if (status == 500 && text.contains("AdminCreateUserRequest")) continue
            if (status == 404 || status == 405) continue
            requireOk("POST $path (user update)", status, text)
            return
        }

        // 3) PATCH without path param (body includes userId).
        val (s3, t3) = execFetch(buildUrl(baseUrl, "/admin/users"), "PATCH", bodyWithId)
        if (s3 in 200..299) return

        // Report the first meaningful error.
        if (s1 != 404 && s1 != 405) {
            requireOk("PATCH /admin/users/{id}", s1, t1)
        }
        error(
            "Could not update user. PATCH /admin/users/$enc returned HTTP $s1. " +
                "The deployed API may need to be rebuilt from this repo."
        )
    }

    suspend fun deleteUser(userId: String) {
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/users/$userId"), "DELETE")
        requireOk("DELETE /admin/users", status, text)
    }

    suspend fun getUserBrands(userId: String): List<String> {
        val enc = userId.trim().replace(" ", "%20")
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/user/$enc/brands"), "GET")
        if (status == 404) return emptyList()
        requireOk("GET /admin/user/$enc/brands", status, text)
        return tolerantJson.decodeFromString<UserBrandsResponseDto>(text).brandCodes
    }

    suspend fun updateMatrix(cells: List<UpdateMatrixCellDto>): UpdateMatrixResponseDto {
        val body = tolerantJson.encodeToString(UpdateMatrixRequestDto(cells))
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/update-matrix"), "POST", body)
        requireOk("POST /admin/update-matrix", status, text)
        return decodeApiJson("POST /admin/update-matrix", text)
    }

    suspend fun getProductLines(): List<ProductLineDto> {
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product-lines"), "GET")
        requireOk("GET /admin/product-lines", status, text)
        return decodeApiJson("GET /admin/product-lines", text)
    }

    suspend fun getCatalogSections(productLineId: String): List<CatalogSectionDto> {
        val url = buildUrl(baseUrl, "/catalog/sections", listOf("product_line_id" to productLineId))
        val (status, text) = execFetch(url, "GET")
        requireOk("GET /catalog/sections", status, text)
        return decodeApiJson("GET /catalog/sections", text)
    }

    suspend fun getPickerValues(attrKey: String, modifiersOnly: Boolean = false): List<String> {
        val params = buildList {
            add("attr_key" to attrKey)
            if (modifiersOnly) add("source" to "modifiers")
        }
        val url = buildUrl(baseUrl, "/admin/picker-values", params)
        val (status, text) = execFetch(url, "GET")
        requireOk("GET /admin/picker-values", status, text)
        return decodeApiJson<PickerValuesDto>("GET /admin/picker-values", text).values
    }

    suspend fun getCastOptions(productLineId: String): List<AdminCastOptionDto> {
        val url = buildUrl(baseUrl, "/admin/casts", listOf("line_id" to productLineId))
        val (status, text) = execFetch(url, "GET")
        requireOk("GET /admin/casts", status, text)
        return decodeApiJson("GET /admin/casts", text)
    }

    suspend fun createCast(request: CreateCastRequestDto): AdminCastOptionDto {
        val body = tolerantJson.encodeToString(request)
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/cast"), "POST", body)
        requireOk("POST /admin/cast", status, text)
        return decodeApiJson("POST /admin/cast", text)
    }

    suspend fun registerPickerValue(request: RegisterPickerValueRequestDto): RegisterPickerValueResponseDto {
        val body = tolerantJson.encodeToString(request)
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/picker-value"), "POST", body)
        requireOk("POST /admin/picker-value", status, text)
        return decodeApiJson("POST /admin/picker-value", text)
    }

    suspend fun getBrandOptions(): List<AdminBrandOptionDto> {
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/brands"), "GET")
        requireOk("GET /admin/brands", status, text)
        return decodeApiJson("GET /admin/brands", text)
    }

    suspend fun getVariants(productId: String): List<VariantDetailDto> {
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product/$productId/variants"), "GET")
        requireOk("GET /admin/product/$productId/variants", status, text)
        return decodeApiJson("GET /admin/product/$productId/variants", text)
    }

    suspend fun searchVariantsPage(page: Int, pageSize: Int, q: String?): PagedAdminVariantsDto {
        val params = mutableListOf(
            "page" to page.coerceAtLeast(1).toString(),
            "page_size" to pageSize.coerceIn(1, 100).toString(),
        )
        q?.trim()?.takeIf { it.isNotEmpty() }?.let { params += "q" to it }
        val url = buildUrl(baseUrl, "/admin/variants", params)
        val (status, text) = execFetch(url, "GET")
        requireOkOrExplain("GET /admin/variants", status, text)
        return decodeApiJson("GET /admin/variants", text)
    }

    suspend fun createProduct(request: CreateProductRequestDto): CreateProductResponseDto {
        val body = tolerantJson.encodeToString(request)
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product"), "POST", body)
        requireOkOrExplain("POST /admin/product", status, text)
        return decodeApiJson("POST /admin/product", text)
    }

    suspend fun updateProduct(request: UpdateProductRequestDto): BoolResultDto {
        val body = tolerantJson.encodeToString(request)
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product"), "PATCH", body)
        if (status !in 200..299) {
            val msg = runCatching {
                tolerantJson.parseToJsonElement(text)
                    .let { it as? kotlinx.serialization.json.JsonObject }
                    ?.get("error")?.toString()?.trim('"')
            }.getOrNull() ?: text.take(200)
            error("Update product failed: $msg")
        }
        return decodeApiJson("PATCH /admin/product", text)
    }

    suspend fun getProductModifiers(productId: String): ProductModifiersDto {
        val pid = productId.trim().removePrefix("\uFEFF").lowercase()
        val (_, text) = execFetch(buildUrl(baseUrl, "/admin/product/$pid/modifiers"), "GET")
        return decodeApiJson("GET /admin/product/$pid/modifiers", text)
    }

    /** Multipart upload; stores file on API disk and returns public image URL. */
    suspend fun uploadProductImage(productId: String, file: File): ProductImageUploadResponseDto {
        val pid = productId.trim().removePrefix("\uFEFF").lowercase()
        return uploadProductImageAt(buildUrl(baseUrl, "/admin/product/image"), pid, file, "POST /admin/product/image")
            ?: uploadProductImageAt(buildUrl(baseUrl, "/admin/product/$pid/image"), pid, file, "POST /admin/product/$pid/image")
            ?: error("Product image upload failed (API may need redeploy with image routes)")
    }

    private suspend fun uploadProductImageAt(
        url: String,
        productId: String,
        file: File,
        label: String,
    ): ProductImageUploadResponseDto? {
        val headers = js("{}")
        headers["Accept"] = "application/json"
        val t = AdminSession.getToken()
        if (!t.isNullOrBlank()) {
            headers["Authorization"] = "Bearer $t"
        }
        val formData = js("new FormData()")
        formData.append("productId", productId)
        formData.append("image", file)
        val init = js("{}")
        init["method"] = "POST"
        init["headers"] = headers
        init["body"] = formData
        val response = window.fetch(url, init.unsafeCast<RequestInit>()).unsafeCast<Promise<Response>>().await()
        val status = response.status.toInt()
        val text = readResponseText(response)
        if (status == 401) {
            AdminSession.clear()
        }
        if (status == 404) return null
        requireOkOrExplain(label, status, text)
        return decodeApiJson(label, text)
    }

    suspend fun deleteProductImage(productId: String): BoolResultDto {
        val pid = productId.trim().removePrefix("\uFEFF").lowercase()
        val body = tolerantJson.encodeToString(AdminProductImageClearRequestDto(pid))
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product/image/clear"), "POST", body)
        if (status == 404) {
            val (status2, text2) = execFetch(buildUrl(baseUrl, "/admin/product/$pid/image"), "DELETE")
            requireOkOrExplain("DELETE /admin/product/$pid/image", status2, text2)
            return decodeApiJson("DELETE /admin/product/$pid/image", text2)
        }
        requireOkOrExplain("POST /admin/product/image/clear", status, text)
        return decodeApiJson("POST /admin/product/image/clear", text)
    }

    suspend fun archiveProduct(productId: String): BoolResultDto {
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product/$productId"), "DELETE")
        return decodeApiJson("DELETE /admin/product", text)
    }

    suspend fun activateProduct(productId: String): BoolResultDto {
        val body = tolerantJson.encodeToString(ActivateProductRequestDto(productId.trim()))
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/product/activate"), "POST", body)
        return decodeApiJson("POST /admin/product/activate", text)
    }

    suspend fun patchVariant(variantId: String, request: PatchVariantRequestDto): BoolResultDto {
        val body = tolerantJson.encodeToString(request)
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/variant/$variantId"), "PATCH", body)
        return decodeApiJson("PATCH /admin/variant", text)
    }

    suspend fun deleteVariant(variantId: String): BoolResultDto {
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/variant/$variantId"), "DELETE")
        return decodeApiJson("DELETE /admin/variant", text)
    }

    suspend fun createModifier(request: CreateModifierRequestDto): CreateModifierResponseDto {
        val body = tolerantJson.encodeToString(request)
        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/modifier"), "POST", body)
        return decodeApiJson("POST /admin/modifier", text)
    }

    suspend fun patchModifier(modifierId: String, request: PatchModifierRequestDto): BoolResultDto {
        val id = modifierId.trim().lowercase()
        if (id.isEmpty()) error("modifierId required")

        fun acceptUpdated(where: String, status: Int, text: String): BoolResultDto? {
            if (status !in 200..299) return null
            val trimmed = text.removePrefix("\uFEFF").trim()
            if (trimmed.isEmpty()) return BoolResultDto(updated = true)
            throwIfHtmlInsteadOfJson(trimmed, where)
            val result = decodeApiJson<BoolResultDto>(where, trimmed)
            // Some gateways strip boolean fields; treat 2xx without explicit failure as success.
            if (result.updated == false) {
                error("modifier was not saved (server reported updated=false).")
            }
            return result.copy(updated = true)
        }

        // Prefer body-based POST — Coolify/Traefik often block or mishandle PATCH on path params.
        val body = tolerantJson.encodeToString(
            ModifierPatchSaveRequestDto(
                modifierId = id,
                priceImpact = request.priceImpact,
                attrValueUr = request.attrValueUr,
                attrKey = request.attrKey,
                attrValue = request.attrValue,
            )
        )
        val postPaths = listOf(
            "/admin/modifier",
            "/admin/modifier/save",
            "/admin/modifier-list/save",
            "/admin/update-modifier",
        )
        var lastDetail = ""
        for (path in postPaths) {
            val (status, text) = execFetch(buildUrl(baseUrl, path), "POST", body)
            if (status == 404 || status == 405 || status == 501) {
                lastDetail = "POST $path → HTTP $status"
                continue
            }
            acceptUpdated("POST $path", status, text)?.let { return it }
            requireOk("POST $path", status, text)
        }

        val patchBody = tolerantJson.encodeToString(request)
        val patchUrl = buildUrl(baseUrl, "/admin/modifier/$id")
        val (patchStatus, patchText) = execFetch(patchUrl, "PATCH", patchBody)
        acceptUpdated("PATCH /admin/modifier/{id}", patchStatus, patchText)?.let { return it }
        if (patchStatus != 404 && patchStatus != 405 && patchStatus != 501) {
            requireOk("PATCH /admin/modifier/{id}", patchStatus, patchText)
        }
        lastDetail = listOfNotNull(
            lastDetail.takeIf { it.isNotEmpty() },
            "PATCH /admin/modifier → HTTP $patchStatus",
        ).joinToString("; ")
        error(
            "$lastDetail. Tried POST ${postPaths.joinToString()} then PATCH /admin/modifier/{id}. " +
                "Redeploy the latest API build.",
        )
    }

    suspend fun deleteModifier(modifierId: String) {
        val id = modifierId.trim().lowercase()
        if (id.isEmpty()) error("modifierId required")

        fun acceptDeleted(where: String, status: Int, text: String): Boolean {
            if (status !in 200..299) return false
            val trimmed = text.removePrefix("\uFEFF").trim()
            if (trimmed.isEmpty()) return true
            throwIfHtmlInsteadOfJson(trimmed, where)
            val r = decodeApiJson<BoolResultDto>(where, trimmed)
            if (r.deleted == false) error("modifier was not deleted.")
            return true
        }

        val body = tolerantJson.encodeToString(DeleteModifierRequestDto(id))
        val postPaths = listOf("/admin/modifier/delete", "/admin/modifier-list/delete")
        var lastDetail = ""
        for (path in postPaths) {
            val (status, text) = execFetch(buildUrl(baseUrl, path), "POST", body)
            if (status == 404 || status == 405 || status == 501) {
                lastDetail = "POST $path → HTTP $status"
                continue
            }
            if (acceptDeleted("POST $path", status, text)) return
            requireOk("POST $path", status, text)
        }

        val (status, text) = execFetch(buildUrl(baseUrl, "/admin/modifier/$id"), "DELETE")
        if (acceptDeleted("DELETE /admin/modifier/{id}", status, text)) return
        if (status != 404 && status != 405 && status != 501) {
            requireOk("DELETE /admin/modifier/{id}", status, text)
        }
        error(
            "${lastDetail.ifEmpty { "DELETE /admin/modifier → HTTP $status" }}. " +
                "Tried POST delete aliases then DELETE /admin/modifier/{id}. Redeploy the latest API build.",
        )
    }

    suspend fun getOrdersPage(page: Int, pageSize: Int, status: String? = null): PagedAdminOrdersDto {
        val params = mutableListOf(
            "page" to page.toString(),
            "page_size" to pageSize.toString(),
        )
        status?.trim()?.takeIf { it.isNotEmpty() }?.let { params += "status" to it.lowercase() }
        val url = buildUrl(baseUrl, "/admin/orders", params)
        val (statusCode, raw) = execFetch(url, "GET")
        val text = raw.removePrefix("\uFEFF").trim()
        requireOkOrExplain("GET /admin/orders", statusCode, text)
        return decodeApiJson("GET /admin/orders", text)
    }

    suspend fun getAdminOrderDetail(orderCode: String): OrderDetailDto {
        val enc = orderCode.replace(" ", "%20")
        val (status, raw) = execFetch(buildUrl(baseUrl, "/admin/orders/$enc"), "GET")
        val text = raw.removePrefix("\uFEFF").trim()
        return decodeApiJson("GET /admin/orders/$orderCode", text)
    }

    suspend fun patchOrderStatus(orderCode: String, statusVal: String) {
        patchOrder(orderCode, status = statusVal.trim().lowercase(), estimatedDelivery = null)
    }

    suspend fun patchOrder(
        orderCode: String,
        status: String? = null,
        estimatedDelivery: String? = null,
    ) {
        val enc = orderCode.replace(" ", "%20")
        val body = tolerantJson.encodeToString(
            PatchOrderStatusRequestDto(
                status = status?.trim()?.takeIf { it.isNotEmpty() },
                estimatedDelivery = estimatedDelivery,
            )
        )
        val (httpStatus, text) = execFetch(buildUrl(baseUrl, "/admin/orders/$enc"), "PATCH", body)
        requireOk("PATCH /admin/orders", httpStatus, text)
    }

    suspend fun markOrderPrinted(orderCode: String) {
        val enc = orderCode.replace(" ", "%20")
        val (httpStatus, text) = execFetch(buildUrl(baseUrl, "/admin/orders/$enc/mark-printed"), "POST", "{}")
        requireOk("POST /admin/orders/.../mark-printed", httpStatus, text)
    }
}

private fun isLoopbackHost(host: String): Boolean =
    host == "localhost" ||
        host == "127.0.0.1" ||
        host == "[::1]" ||
        host.endsWith(".localhost")

/**
 * Fixes common paste typos (e.g. `https://http//127.0.0.1:8080`, `http//127.0.0.1:8080`).
 * Local Ktor uses plain HTTP on loopback.
 */
private fun normalizeApiBaseUrl(raw: String): String {
    var s = raw.trim().trimEnd('/')
    if (s.isEmpty()) return s
    s = s.replace("http//", "http://").replace("https//", "https://")
    val loop = Regex("""https?://(?:127\.0\.0\.1|localhost)(?::\d+)?""").find(s)
    if (loop != null) {
        return loop.value.trimEnd('/').replaceFirst("https://", "http://")
    }
    return s
}

private fun detectBaseUrl(): String {
    val configured = runCatching { js("globalThis.ADMIN_API_BASE_URL") as? String }.getOrNull()
    val trimmed = configured?.trim().orEmpty()
    if (trimmed.isNotEmpty()) return normalizeApiBaseUrl(trimmed)

    /*
     * Empty ADMIN_API_BASE_URL ⇒ relative URLs (/admin/…). On the webpack dev server that often
     * returns index.html (200) instead of proxying, so JSON parsing fails. For loopback hosts,
     * point at the default Ktor port :8080 whenever the page itself is not already on :8080.
     */
    return runCatching {
        val host = window.location.hostname
        if (!isLoopbackHost(host)) return@runCatching ""
        val port = window.location.port
        val proto = window.location.protocol
        if (port == "8080") return@runCatching ""
        "${proto}//${host}:8080".trimEnd('/')
    }.getOrDefault("")
}

private fun requireOk(what: String, status: Int, text: String) {
    if (status !in 200..299) {
        val staleHint =
            if (status == 404 && (what.contains("product-lines") || what.contains("picker-values"))) {
                " This usually means the API process is an old build: stop it, then from the project root run ./gradlew :server:run (or rebuild in Android Studio) so routes like /admin/product-lines are registered."
            } else {
                ""
            }
        val hint = text.take(200).trim().ifEmpty { "" }
        error("$what → HTTP $status $hint$staleHint")
    }
}
